/**
 * Single data module for /research/gulf-digital-marketing-statistics.
 * The page tables, FAQ, JSON-LD, crawler summary and the CSV written by
 * scripts/build-gulf-stats-csv.mjs all read from here.
 *
 * Every value is copied from the verified stats list. Do not round, convert
 * or combine values here. Jamil Global first-party figures are not rows; they
 * are listed in FIRST_PARTY_STATS and never enter the CSV or the Dataset.
 */

export const GULF_STATS_PATH = "/research/gulf-digital-marketing-statistics";
export const GULF_STATS_CSV_PATH = "/research/gulf-digital-marketing-statistics.csv";
export const GULF_STATS_UPDATED = "2026-11-01";
export const GULF_STATS_UPDATED_LABEL = "Nov 1, 2026";
export const GULF_STATS_LICENSE = "https://creativecommons.org/licenses/by/4.0/";
export const GULF_STATS_CONTACT_EMAIL = "hello@jamilglobal.com";
export const GULF_STATS_COVERAGE = "2025-10/2026-06";

export const GULF_STATS_CSV_HEADER = [
  "id",
  "section",
  "stat",
  "country",
  "value",
  "period",
  "publisher",
  "source_title",
  "source_url",
  "published",
  "checked",
];

const MS_Q2_URL =
  "https://www.microsoft.com/en-us/research/wp-content/uploads/2026/09/Microsoft-AI-Diffusion-Report-2026-Q2.pdf";

/**
 * Sources, keyed by the code used on each row. `published` is ISO (YYYY-MM-DD),
 * or YYYY-MM when the publisher gives only a month.
 */
export const SOURCES = {
  "DR-KW": {
    code: "DR-KW",
    label: "DataReportal, Digital 2026: Kuwait",
    publisher: "DataReportal (Kepios)",
    title: "Digital 2026: Kuwait",
    byline: "Simon Kemp",
    published: "2025-11-08",
    dataPeriod: "Oct and late 2025",
    url: "https://datareportal.com/reports/digital-2026-kuwait",
  },
  "DR-AE": {
    code: "DR-AE",
    label: "DataReportal, Digital 2026: The United Arab Emirates",
    publisher: "DataReportal (Kepios, with Meltwater and We Are Social)",
    title: "Digital 2026: The United Arab Emirates",
    byline: "Simon Kemp",
    published: "2025-11-05",
    dataPeriod: "Oct 2025 and late 2025",
    url: "https://datareportal.com/reports/digital-2026-united-arab-emirates",
  },
  "DR-SA": {
    code: "DR-SA",
    label: "DataReportal, Digital 2026: Saudi Arabia",
    publisher: "DataReportal (Kepios)",
    title: "Digital 2026: Saudi Arabia",
    byline: "Simon Kemp",
    published: "2025-11-08",
    dataPeriod: "Late 2025",
    url: "https://datareportal.com/reports/digital-2026-saudi-arabia",
  },
  "DR-QA": {
    code: "DR-QA",
    label: "DataReportal, Digital 2026: Qatar",
    publisher: "DataReportal (Kepios)",
    title: "Digital 2026: Qatar",
    byline: "Simon Kemp",
    published: "2025-11-08",
    dataPeriod: "Late 2025",
    url: "https://datareportal.com/reports/digital-2026-qatar",
  },
  "MS-Q2": {
    code: "MS-Q2",
    label: "Microsoft AI Economy Institute, Global AI Diffusion Q2 2026",
    publisher: "Microsoft AI Economy Institute",
    title: "Global AI Diffusion: Q2 2026 Trends and Insights",
    byline: "Microsoft AI Economy Institute",
    published: "2026-09",
    dataPeriod: "June 2026",
    url: MS_Q2_URL,
  },
};

export const SECTIONS = {
  A: "Internet and mobile",
  B: "Social media",
  C: "Platform ad reach (late 2025)",
  D: "Generative AI adoption",
};

const row = (id, section, stat, country, value, period, sourceCode) => ({
  id,
  section,
  stat,
  country,
  value,
  period,
  sourceCode,
  published: SOURCES[sourceCode].published,
  checked: GULF_STATS_UPDATED,
});

export const GULF_STATS = [
  // A. Internet and mobile
  row("kw-internet-users", "A", "Internet users", "Kuwait", "5.00M, 99.0% penetration (capped at 99%)", "Oct 2025", "DR-KW"),
  row("ae-internet-users", "A", "Internet users", "UAE", "11.3M, 99.0% penetration", "Oct 2025", "DR-AE"),
  row("sa-internet-users", "A", "Internet users", "Saudi Arabia", "34.4M, 99.0% penetration", "Oct 2025", "DR-SA"),
  row("qa-internet-users", "A", "Internet users", "Qatar", "3.10M, 99.0% penetration", "Oct 2025", "DR-QA"),
  row("kw-cellular-connections", "A", "Cellular connections (GSMA Intelligence via DataReportal)", "Kuwait", "7.72M, 153% of population", "Late 2025", "DR-KW"),
  row("ae-cellular-connections", "A", "Cellular connections (GSMA Intelligence via DataReportal)", "UAE", "23.0M, 202% of population", "Late 2025", "DR-AE"),
  row("sa-cellular-connections", "A", "Cellular connections (GSMA Intelligence via DataReportal)", "Saudi Arabia", "48.7M, 140%", "Late 2025", "DR-SA"),
  row("qa-cellular-connections", "A", "Cellular connections (GSMA Intelligence via DataReportal)", "Qatar", "4.75M, 152%", "Late 2025", "DR-QA"),
  row("kw-mobile-download-speed", "A", "Median mobile download speed (Ookla via DataReportal)", "Kuwait", "414.56 Mbps", "Late 2025", "DR-KW"),
  row("ae-mobile-download-speed", "A", "Median mobile download speed (Ookla via DataReportal)", "UAE", "614.42 Mbps", "Late 2025", "DR-AE"),
  row("sa-mobile-download-speed", "A", "Median mobile download speed (Ookla via DataReportal)", "Saudi Arabia", "194.49 Mbps", "Late 2025", "DR-SA"),
  row("qa-mobile-download-speed", "A", "Median mobile download speed (Ookla via DataReportal)", "Qatar", "511.35 Mbps", "Late 2025", "DR-QA"),

  // B. Social media
  row("kw-social-media-users", "B", "Social media user identities", "Kuwait", "4.44M, 87.9% of population", "Oct 2025", "DR-KW"),
  row("ae-social-media-users", "B", "Social media user identities", "UAE", "12.5M, 110% of population (source flags this as above 100%)", "Oct 2025", "DR-AE"),
  row("sa-social-media-users", "B", "Social media user identities", "Saudi Arabia", "38.6M, 111% of population", "Late 2025", "DR-SA"),
  row("qa-social-media-users", "B", "Social media user identities", "Qatar", "2.95M, 94.3%", "Late 2025", "DR-QA"),
  row("kw-social-media-growth", "B", "Social media user identities, growth", "Kuwait", "+15.0%", "Late 2024 to end 2025", "DR-KW"),
  row("ae-social-media-growth", "B", "Social media user identities, growth", "UAE", "+16.7%", "Late 2024 to end 2025", "DR-AE"),
  row("sa-social-media-growth", "B", "Social media user identities, growth", "Saudi Arabia", "+17.7%", "Late 2024 to end 2025", "DR-SA"),
  row("qa-social-media-growth", "B", "Social media user identities, growth", "Qatar", "+16.1%", "Late 2024 to end 2025", "DR-QA"),

  // C. Platform ad reach (ad tools as reported by DataReportal, not monthly active users)
  row("kw-instagram-ad-reach", "C", "Instagram ad reach", "Kuwait", "3.00M, 59.4% of population", "Late 2025", "DR-KW"),
  row("kw-snapchat-ad-reach", "C", "Snapchat ad reach", "Kuwait", "2.43M, 48.2% of population", "Late 2025", "DR-KW"),
  row("kw-youtube-ad-reach", "C", "YouTube ad reach", "Kuwait", "3.31M, 65.6% of population", "Late 2025", "DR-KW"),
  row("kw-linkedin-members", "C", "LinkedIn members", "Kuwait", "1.30M members", "Late 2025", "DR-KW"),
  row("kw-tiktok-ad-reach", "C", "TikTok ad reach", "Kuwait", "4.44M adults 18+", "Late 2025", "DR-KW"),
  row("sa-snapchat-ad-reach", "C", "Snapchat ad reach", "Saudi Arabia", "25.3M, 72.9% of population, 89.0% of adults", "Late 2025", "DR-SA"),
  row("sa-youtube-ad-reach", "C", "YouTube ad reach", "Saudi Arabia", "27.5M, 79.2%", "Late 2025", "DR-SA"),
  row("sa-x-ad-reach", "C", "X ad reach", "Saudi Arabia", "15.0M, 43.1%", "Late 2025", "DR-SA"),
  row("ae-facebook-ad-reach", "C", "Facebook ad reach", "UAE", "9.70M, 85.0% of population", "Late 2025", "DR-AE"),
  row("ae-instagram-ad-reach", "C", "Instagram ad reach", "UAE", "8.05M, 70.5% of population", "Late 2025", "DR-AE"),
  row("ae-linkedin-members", "C", "LinkedIn members", "UAE", "10.0M members, 87.6% of population", "Late 2025", "DR-AE"),
  row("qa-facebook-ad-reach", "C", "Facebook ad reach", "Qatar", "2.40M, 76.6%", "Late 2025", "DR-QA"),
  row("qa-linkedin-members", "C", "LinkedIn members", "Qatar", "1.80M members, 57.5%", "Late 2025", "DR-QA"),
  row("qa-youtube-ad-reach", "C", "YouTube ad reach", "Qatar", "2.31M, 73.8%", "Late 2025", "DR-QA"),

  // D. Generative AI adoption (share of people aged 15 to 64 who used a generative AI product)
  row("ae-genai-adoption", "D", "Generative AI adoption, share of people aged 15 to 64", "UAE", "73.3%, ranked #1 globally (up 3.2pp from 70.1% in Q1 2026)", "June 2026", "MS-Q2"),
  row("qa-genai-adoption", "D", "Generative AI adoption, share of people aged 15 to 64", "Qatar", "43.5%, ranked #10 (up 1.7pp)", "June 2026", "MS-Q2"),
  row("sa-genai-adoption", "D", "Generative AI adoption, share of people aged 15 to 64", "Saudi Arabia", "31.4%, ranked #25 (up 2.0pp from 29.4%; largest rise in rank)", "June 2026", "MS-Q2"),
  row("kw-genai-adoption", "D", "Generative AI adoption, share of people aged 15 to 64", "Kuwait", "22.6% (up 1.5pp from 21.1%)", "June 2026", "MS-Q2"),
  row("world-genai-adoption", "D", "Generative AI adoption, share of people aged 15 to 64", "World", "18.8% average (from 17.8% in Q1 2026)", "June 2026", "MS-Q2"),
];

/** Jamil Global, first-party. Labelled on the page only. Not in the CSV or the Dataset. */
export const FIRST_PARTY_STATS = [
  { value: "$15M+", label: "in ad spend managed" },
  { value: "71 and counting", label: "clients" },
  { value: "18 to 26%", label: "ROI on that spend" },
];

/** Headline figures for the short answer block, the crawler summary and the Dataset description. */
export const HEADLINE_FIGURES = [
  "Kuwait: 5.00M internet users, 99.0% penetration (capped at 99%), Oct 2025. Source: DataReportal, Digital 2026: Kuwait.",
  "UAE: 73.3% of people aged 15 to 64 used a generative AI product in June 2026, ranked #1 globally. Source: Microsoft AI Economy Institute.",
  "Kuwait: 22.6% of people aged 15 to 64 in June 2026, against a world figure of 18.8%. Source: Microsoft AI Economy Institute.",
];

export const PLATFORM_CAVEAT =
  "Platform audience figures come from advertising tools as reported by DataReportal. They are ad reach, not monthly active users, and can exceed 100% of a population because of duplicate accounts, visitors and platform revisions. We republish them as the source states them.";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "2025-11-05" becomes "5 Nov 2025"; "2026-09" becomes "September 2026". */
export function formatPublished(iso) {
  const [year, month, day] = iso.split("-");
  if (!day) return `${MONTHS_LONG[Number(month) - 1]} ${year}`;
  return `${Number(day)} ${MONTHS_SHORT[Number(month) - 1]} ${year}`;
}

/** "2026-11-01" becomes "Nov 1, 2026". */
export function formatChecked(iso) {
  const [year, month, day] = iso.split("-");
  return `${MONTHS_SHORT[Number(month) - 1]} ${Number(day)}, ${year}`;
}

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** CSV mirror of GULF_STATS. Written to public/ by scripts/build-gulf-stats-csv.mjs. */
export function buildGulfStatsCsv() {
  const lines = GULF_STATS.map((stat) => {
    const source = SOURCES[stat.sourceCode];
    return [
      stat.id,
      stat.section,
      stat.stat,
      stat.country,
      stat.value,
      stat.period,
      source.publisher,
      source.title,
      source.url,
      stat.published,
      stat.checked,
    ]
      .map(csvCell)
      .join(",");
  });
  return [GULF_STATS_CSV_HEADER.join(","), ...lines].join("\n") + "\n";
}

export const gulfStatsFaqs = [
  {
    question: "How many internet users does Kuwait have?",
    answer:
      "For Oct 2025, DataReportal's Digital 2026: Kuwait report gives 5.00M internet users in Kuwait, an internet penetration of 99.0% (capped at 99%).",
  },
  {
    question: "Which Gulf country leads generative AI adoption?",
    answer:
      "Of the four countries on this page, the UAE has the highest share. In June 2026, 73.3% of people aged 15 to 64 used a generative AI product, ranked #1 globally by Microsoft's AI Economy Institute. Qatar is at 43.5%, Saudi Arabia at 31.4% and Kuwait at 22.6%.",
  },
  {
    question: "Why can social media reach exceed 100%?",
    answer:
      "The platform figures are ad reach from advertising tools, as reported by DataReportal. They are not monthly active users, and they can exceed 100% of a population because of duplicate accounts, visitors and platform revisions. The UAE social media figure, 12.5M user identities at 110% of population, is flagged in the source as above 100%.",
  },
  {
    question: "How often is this page updated?",
    answer:
      "It was last updated Nov 1, 2026. Microsoft's AI Diffusion figures are reviewed quarterly, the DataReportal platform block is replaced annually when Digital 2027 country reports are published, and every source link is checked monthly. The changelog on this page lists each change.",
  },
  {
    question: "Can I reuse these statistics?",
    answer:
      "Yes. The compilation on this page is licensed under CC BY 4.0, so you can reuse it with attribution. Cite the original source linked beside each third-party figure as well as this page.",
  },
];
